import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { RootState } from '../store/store';
import { setCollections } from '../store/collectionsSlice';
import { collectionsService } from '../services/collectionsService';
import { statsService } from '../services/statsService';
import { useTheme } from '../context/ThemeContext';
import {
  PlusCircleIcon,
  BookOpenIcon,
  ClockIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  BoltIcon,
  SparklesIcon,
  ChartBarIcon,
  BeakerIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { PerformanceChart } from '../components/PerformanceChart';

interface Collection {
  _id: string;
  title: string;
  description: string;
  flashcardCount: number;
  updatedAt: string;
}

interface UserStats {
  loginStreak: number;
  totalQuizzesTaken: number;
  totalQuizScore: number;
  averageQuizScore: number;
  collectionsCreated: number;
  flashcardsCreated: number;
  lastLoginDate: string;
  bestQuizScore: number;
  worstQuizScore: number;
  quizHistory?: { score: number; date: string; }[];
}

export const PremiumDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const collections = useSelector((state: RootState) => state.collections.collections);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const { darkMode } = useTheme();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Redirect to regular dashboard if not premium
    if (!user.isPremium) {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [collectionsData, statsData, streakUpdate] = await Promise.all([
          collectionsService.getCollections(),
          statsService.getUserStats(),
          statsService.updateLoginStreak() // Add this line
        ]);
        
        dispatch(setCollections(collectionsData));
        setStats(statsData);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user, dispatch, navigate]);

  // Calculate average quiz score
  const averageScore = stats && stats.totalQuizzesTaken > 0
    ? Math.min(100, Math.round((stats.totalQuizScore / stats.totalQuizzesTaken)))
    : 0;

  if (!user) return null;

  const generateAIQuiz = () => {
    setIsAIGenerating(true);
    // Simulate AI generation
    setTimeout(() => {
      setIsAIGenerating(false);
      navigate('/quiz/create');
    }, 2000);
  };

  const recentCollections = [...collections]  // Create a copy of the array
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Enhanced Background Pattern for Pro Users */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200 dark:bg-purple-900/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-indigo-200 dark:bg-indigo-900/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-200 dark:bg-pink-900/30 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-200 dark:bg-blue-900/10 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        <svg className="absolute inset-0 h-full w-full stroke-gray-200 dark:stroke-gray-700/30 opacity-20" style={{ maskImage: 'radial-gradient(circle at center, transparent 30%, black)' }}>
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M0 40V0M40 40V0" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <NavigationBar />
      <div className="pt-20 pb-12 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center h-64"
              >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-8"
              >
                {/* Premium Welcome Section */}
                <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                          Welcome back, {user.username}!
                        </h1>
                        <div className="ml-3 px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold rounded-full flex items-center">
                          <SparklesIcon className="h-3 w-3 mr-1" />
                          PRO
                        </div>
                      </div>
                      <p className="mt-2 text-gray-600 dark:text-gray-300">
                        Enjoy your enhanced premium experience
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={generateAIQuiz}
                        disabled={isAIGenerating}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-md disabled:opacity-50`}
                      >
                        {isAIGenerating ? (
                          <>
                            <div className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <BeakerIcon className="h-5 w-5 mr-2" />
                            Generate AI Quiz
                          </>
                        )}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/quiz/create')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-md"
                      >
                        <PlusCircleIcon className="h-5 w-5 mr-2" />
                        Create Collection
                      </motion.button>
                    </div>
                  </div>

                  {/* Enhanced Stats Grid */}
                  <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50">
                      <div className="flex items-center">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
                          <BookOpenIcon className="h-8 w-8 text-indigo-500 dark:text-indigo-400" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Collections</p>
                          <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats?.collectionsCreated || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Unlimited available</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50">
                      <div className="flex items-center">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
                          <DocumentTextIcon className="h-8 w-8 text-purple-500" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Flashcards</p>
                          <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats?.flashcardsCreated || 0}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Unlimited available</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50">
                      <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
                          <BoltIcon className="h-8 w-8 text-yellow-500" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Study Streak</p>
                          <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats?.loginStreak || 0} days</p>
                          <p className="text-xs text-green-500">+{Math.min(stats?.loginStreak || 0, 7)} day bonus</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50">
                      <div className="flex items-center">
                        <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                          <AcademicCapIcon className="h-8 w-8 text-green-500" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Avg. Score</p>
                          <p className="text-2xl font-semibold text-gray-900 dark:text-white">{averageScore}%</p>
                          <div className="flex items-center">
                            <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                            <p className="text-xs text-green-500">+3% this week</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Collections with Premium Styling */}
                <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Collections</h2>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/quizzes')}
                      className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                      View All
                    </motion.button>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {recentCollections.length > 0 ? (
                      recentCollections.map((collection) => (
                        <motion.div
                          key={collection._id}
                          whileHover={{ y: -4, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}
                          className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-700 dark:to-indigo-900/30 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {collection.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                            {collection.description}
                          </p>
                          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              <span>{collection.flashcardCount} cards</span>
                            </div>
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              <span>{new Date(collection.updatedAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="mt-4 flex space-x-3">
                            <button
                              onClick={() => navigate(`/quiz/${collection._id}`)}
                              className="w-full px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 dark:from-indigo-600 dark:to-purple-700 dark:hover:from-indigo-700 dark:hover:to-purple-800"
                            >
                              Practice Quiz
                            </button>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-3 text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400">You haven't created any collections yet.</p>
                        <button
                          onClick={() => navigate('/quiz/create')}
                          className="mt-4 px-4 py-2 text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50"
                        >
                          Create Your First Collection
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium Analytics */}
                <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50">
                  <div className="flex items-center mb-6">
                    <ChartBarIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Premium Analytics</h2>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Quiz Performance</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Quizzes Taken</p>
                          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{stats?.totalQuizzesTaken || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Average Score</p>
                          <p className="mt-1 text-2xl font-semibold text-indigo-600 dark:text-indigo-400">{averageScore}%</p>
                        </div>
                      </div>
                      <div className="mt-4 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-600"
                          style={{ width: `${averageScore}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Progress Over Time Chart */}
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Progress Over Time</p>
                      <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50">
                        <PerformanceChart stats={stats} />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};